// Master reverb — the impulse response and the send law, kept pure so the
// shape is testable without an AudioContext.
//
// The room is SYNTHESISED, not sampled: exponentially decaying noise, decorrelated
// per channel so the tail has width, with a short pre-delay so the dry attack
// stays in front of the wash. A convolver over that reads as a small hall —
// what a backing track wants under a comp — and costs one buffer per session.

/** Seconds of tail; a jazz room, not a cathedral. */
export const REVERB_SECONDS = 1.9;
/** Exponent of the decay: the tail is 60 dB down at the end of the buffer. */
const REVERB_DECAY = 3.2;
/** Dry-to-wet distance: the first reflections arrive after this many seconds. */
export const REVERB_PREDELAY_SECONDS = 0.018;
/** Send gain at the slider's top. Full-scale wet would drown the comp. */
export const REVERB_MAX_SEND = 0.55;

/** The send-bus gain for a slider amount in 0..1: quadratic so the bottom of the
 * slider is a touch of air rather than half a hall, and the top is `REVERB_MAX_SEND`. */
export function reverbSendGain(amount: number): number {
  const a = Math.min(1, Math.max(0, Number.isFinite(amount) ? amount : 0));
  return REVERB_MAX_SEND * a * a;
}

/** A deterministic uniform noise source (mulberry32) so an impulse is the same
 * on every build — a test can pin its envelope, and two channels seeded apart
 * are decorrelated without being random across sessions. */
function noise(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fill one channel of an impulse response in place: silence through the
 * pre-delay, then noise under an exponential decay. `seed` decorrelates channels. */
export function fillReverbImpulse(
  channel: Float32Array,
  sampleRate: number,
  seed: number,
  seconds: number = REVERB_SECONDS,
  predelay: number = REVERB_PREDELAY_SECONDS,
): void {
  const rand = noise(seed);
  const start = Math.min(channel.length, Math.round(predelay * sampleRate));
  const tail = Math.max(1, Math.round(seconds * sampleRate) - start);
  for (let i = 0; i < start; i++) channel[i] = 0;
  for (let i = start; i < channel.length; i++) {
    const t = (i - start) / tail;
    const env = t >= 1 ? 0 : Math.pow(1 - t, REVERB_DECAY);
    channel[i] = (rand() * 2 - 1) * env;
  }
}

/** Build the stereo impulse buffer on a context. */
export function buildReverbImpulse(ctx: BaseAudioContext): AudioBuffer {
  const length = Math.round(REVERB_SECONDS * ctx.sampleRate);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  fillReverbImpulse(buffer.getChannelData(0), ctx.sampleRate, 0x9e3779b9);
  fillReverbImpulse(buffer.getChannelData(1), ctx.sampleRate, 0x7f4a7c15);
  return buffer;
}
